/**
 * Tweerichtings-sticky filterkolom (README §5.2).
 *
 * Past de kolom in het scherm, dan is het gewoon `position: sticky` onder de
 * header. Is hij langer — met acht filtergroepen is dat bijna altijd zo — dan:
 *
 *   omlaag scrollen  -> de kolom loopt mee tot zijn ONDERKANT in beeld is en
 *                       blijft daar hangen, zodat je de laatste filters ziet
 *   omhoog scrollen  -> hij loopt mee tot zijn BOVENKANT onder de header zit
 *                       en blijft daar, zodat je terug bent bij Herkomst
 *
 * Overgenomen uit `onScroll` in het prototype, met twee aanpassingen:
 *
 *  - De hoogte van de header wordt gemeten in plaats van vastgezet op 132px;
 *    dit thema heeft een andere header en die verandert mee met de instellingen.
 *  - Horizon scrollt niet het venster maar een container (`.page-wrapper`), dus
 *    `window.scrollY` blijft 0 en er komen geen scroll-events op window binnen.
 *    Het script zoekt daarom de scrollende voorouder op.
 */

const GAT = 24; // ademruimte onder de header en boven de onderrand
const MINIMALE_BREEDTE = 750; // daaronder zitten de filters in een lade
const DREMPEL = 4; // kleine bewegingen niet van richting laten wisselen

/** Dichtstbijzijnde voorouder die daadwerkelijk scrollt, of window. */
function scrollWortel(el) {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const overflow = getComputedStyle(p).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && p.scrollHeight > p.clientHeight) {
      return p;
    }
  }
  return window;
}

class StickyKolom {
  #el;
  #ouder;
  #wortel;
  #vorigeY;
  #modus = null;

  constructor(el) {
    this.#el = el;
    this.#ouder = el.parentElement;
    this.#wortel = scrollWortel(el);
    this.#vorigeY = this.#positie();
    // `position: sticky` staat in CSS (.facets-block-wrapper--vertical in
    // smokkelaar-styles), niet inline: Horizons morph zet de inline stijl bij
    // elke filterwissel terug en dan was de kolom niet meer sticky (ronde 2, B8).
    this.#bijScroll();

    this.#wortel.addEventListener('scroll', this.#bijScroll, { passive: true });
    addEventListener('resize', this.#bijHerschalen, { passive: true });
  }

  #positie() {
    return this.#wortel === window ? scrollY : this.#wortel.scrollTop;
  }

  #zichtbareHoogte() {
    return this.#wortel === window ? innerHeight : this.#wortel.clientHeight;
  }

  #headerHoogte() {
    // Horizons header-component, niet het eerste <header> in de DOM: op
    // pagina's met een eigen hero-<header> zou dat de verkeerde zijn.
    const header = document.querySelector('header-component') || document.querySelector('.header');
    if (!header) return 0;
    const pos = getComputedStyle(header).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    return header.offsetHeight;
  }

  #bijHerschalen = () => {
    this.#modus = null;
    this.#bijScroll();
  };

  #bijScroll = () => {
    const el = this.#el;
    const ouder = this.#ouder;
    if (!el || !ouder) return;

    if (innerWidth < MINIMALE_BREEDTE) {
      el.style.top = '';
      el.style.marginTop = '';
      this.#modus = null;
      return;
    }

    const y = this.#positie();
    const verschil = y - this.#vorigeY;
    // De drempel remt alleen het wisselen van richting; de marge-correctie moet
    // altijd draaien, anders blijft hij hangen als een scroll uitrolt in kleine
    // stapjes en houdt de kolom een gat boven zich.
    const wisselt = Math.abs(verschil) >= DREMPEL;
    const omlaag = verschil > 0;
    if (wisselt) this.#vorigeY = y;

    const vh = this.#zichtbareHoogte();
    const hoogte = el.offsetHeight;
    const boven = this.#headerHoogte();

    // Past hij in beeld? Dan volstaat gewoon vastzetten onder de header.
    if (hoogte <= vh - boven - GAT) {
      el.style.top = boven + GAT + 'px';
      el.style.marginTop = '0px';
      this.#modus = 'boven';
      return;
    }

    const eigen = el.getBoundingClientRect();
    const ouderRect = ouder.getBoundingClientRect();
    const maxMarge = Math.max(0, ouder.offsetHeight - hoogte);
    const zetMarge = (m) => {
      el.style.marginTop = Math.min(maxMarge, Math.max(0, m)) + 'px';
    };

    // Bovenaan de pagina hoort er nooit een marge te staan.
    if (y <= 0) {
      el.style.marginTop = '0px';
      el.style.top = boven + GAT + 'px';
      this.#modus = 'boven';
      return;
    }

    if (wisselt && omlaag) {
      if (this.#modus !== 'onder') {
        this.#modus = 'onder';
        zetMarge(eigen.top - ouderRect.top);
        el.style.top = vh - hoogte - GAT + 'px';
      }
    } else if (wisselt && this.#modus !== 'boven') {
      this.#modus = 'boven';
      zetMarge(eigen.top - ouderRect.top);
      el.style.top = boven + GAT + 'px';
    } else if (this.#modus === 'boven' && eigen.top > boven + GAT) {
      zetMarge(boven + GAT - ouderRect.top);
    }
  };
}

// Het grid-item, niet de binnenste wrapper: sticky schuift binnen de ouder, en
// de binnenste wrapper is even hoog als zijn eigen ouder. Het grid-item krijgt
// in CSS align-self: start, anders rekt het uit tot de volle hoogte van het
// grid en is er alsnog niets om in te schuiven.
const kolom = document.querySelector('.facets-block-wrapper--vertical');
if (kolom) new StickyKolom(kolom);
